import { useEffect, useState } from 'react';

import axiosInstance from '@/configs/axios';
import { useAuth } from '@/hooks/use-auth';

type TicketState = 'waiting' | 'ready';

declare global {
  interface Window {
    KANG_TOKEN?: string;
  }
}

// Teto para a espera do bilhete. Identificar quem fala é melhoria; a bolha de suporte é o
// serviço, e ela não pode depender da nossa API responder. `axiosInstance` não tem timeout,
// então sem este teto uma requisição pendurada deixaria o usuário sem canal nenhum.
const TICKET_BUDGET_MS = 1500;

/**
 * `kang.js` lê `window.KANG_TOKEN` uma única vez, no próprio init, e não expõe
 * método de identificar depois. Por isso a global tem que estar publicada antes
 * de o script executar, e é o retorno 'ready' que libera a renderização dele.
 */
export function useKangTicket(enabled: boolean): TicketState {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [state, setState] = useState<TicketState>('waiting');

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let released = false;

    const release = () => {
      if (cancelled || released) return;
      released = true;
      setState('ready');
    };

    // Visitante deslogado não tem o que identificar: pedir bilhete devolveria 401 em toda
    // visita. Cache local vazio com sessão viva na API existe (logout em outra aba, storage
    // limpo) e nesse caso o chat fica anônimo — barato agora que o anônimo é o normal, e o
    // troco é não segurar a bolha nem inundar o log com 401 no caminho mais percorrido.
    if (userId === null) {
      delete window.KANG_TOKEN;
      setState('ready');
      return undefined;
    }

    setState('waiting');
    const timer = window.setTimeout(release, TICKET_BUDGET_MS);

    axiosInstance
      .get('/kang-ticket')
      .then((response) => {
        const token = response.data?.data?.token;
        // Publicamos mesmo depois do prazo: `afterInteractive` costuma demorar mais que ele,
        // então o bilhete atrasado ainda alcança o init do kang.js. Se não alcançar, sobra
        // uma global ignorada.
        if (!cancelled && token) window.KANG_TOKEN = token;
      })
      .catch(() => {
        console.warn('[kang] identity ticket unavailable; the chat stays anonymous');
        if (!cancelled) delete window.KANG_TOKEN;
      })
      .finally(release);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enabled, userId]);

  return state;
}
